<template>
  <div class="smart-schedule-container py-2">
    <vue-element-loading :active="submiting" />

    <!-- Skeleton Loading State -->
    <v-card v-if="loading" class="pa-8 text-center rounded-xl elevation-1 my-4 border">
      <v-progress-circular indeterminate color="primary" size="64" width="5" class="mb-4"></v-progress-circular>
      <div class="text-h6 font-weight-bold primary--text">جاري حساب أوقات الرحلة من الخريطة...</div>
      <div class="text-caption grey--text mt-1">يتم طلب مسار الخريطة وتوزيع أوقات الوصول تراكمياً بين المحطات</div>
    </v-card>

    <!-- Main Content Area -->
    <div v-else>
      <!-- Header Bar -->
      <div class="d-flex flex-wrap align-center justify-space-between mb-4">
        <div>
          <div class="d-flex align-center">
            <h2 class="text-h5 font-weight-bold text-dark mb-1">تكوين جدول المواعيد الذكي</h2>
          </div>
          <p class="text-subtitle-2 grey--text text--darken-1 mb-0">
            يتم حساب أوقات الوصول تلقائياً من مسار الرحلة على الخريطة بعد اختيار المحطات ووقت بداية الرحلة.
          </p>
        </div>

        <!-- Status Badge -->
        <div>
          <v-chip
            v-if="timingSource === 'map'"
            color="green lighten-5"
            text-color="success darken-2"
            class="font-weight-bold px-4 py-2 rounded-pill border-success"
          >
            <v-icon left color="success">mdi-check-circle</v-icon>
            الحساب تلقائي من الخريطة
          </v-chip>

          <v-chip
            v-else-if="timingSource === 'manual_fallback'"
            color="amber lighten-5"
            text-color="amber darken-4"
            class="font-weight-bold px-4 py-2 rounded-pill border-amber"
          >
            <v-icon left color="amber darken-3">mdi-alert</v-icon>
            استخدام التوقيت الاحتياطي
          </v-chip>

          <v-chip
            v-else
            color="red lighten-5"
            text-color="error"
            class="font-weight-bold px-4 py-2 rounded-pill border-error"
          >
            <v-icon left color="error">mdi-alert-circle</v-icon>
            تعذر حساب الأوقات
          </v-chip>
        </div>
      </div>

      <!-- Top KPI Cards Row (5 Cards) -->
      <v-row class="mb-4">
        <!-- Card 1: بداية الرحلة -->
        <v-col cols="12" sm="6" md="2" class="py-1">
          <v-card class="pa-3 rounded-xl elevation-1 border d-flex align-center justify-space-between">
            <div>
              <div class="text-caption grey--text font-weight-medium">بداية الرحلة</div>
              <div class="text-h6 font-weight-bold primary--text">{{ formattedStartTime }}</div>
            </div>
            <v-avatar color="deep-purple lighten-5" size="42">
              <v-icon color="primary">mdi-clock-outline</v-icon>
            </v-avatar>
          </v-card>
        </v-col>

        <!-- Card 2: مدة الطريق -->
        <v-col cols="12" sm="6" md="3" class="py-1">
          <v-card class="pa-3 rounded-xl elevation-1 border d-flex align-center justify-space-between">
            <div>
              <div class="text-caption grey--text font-weight-medium">مدة الطريق</div>
              <div class="text-h6 font-weight-bold text-dark">{{ formattedRoadTime }}</div>
            </div>
            <v-avatar color="blue lighten-5" size="42">
              <v-icon color="blue darken-2">mdi-routes</v-icon>
            </v-avatar>
          </v-card>
        </v-col>

        <!-- Card 3: إجمالي الانتظار -->
        <v-col cols="12" sm="6" md="2" class="py-1">
          <v-card class="pa-3 rounded-xl elevation-1 border d-flex align-center justify-space-between">
            <div>
              <div class="text-caption grey--text font-weight-medium">إجمالي الانتظار</div>
              <div class="text-h6 font-weight-bold text-dark">{{ formattedWaitTime }}</div>
            </div>
            <v-avatar color="orange lighten-5" size="42">
              <v-icon color="orange darken-2">mdi-timer-sand-outline</v-icon>
            </v-avatar>
          </v-card>
        </v-col>

        <!-- Card 4: الوصول المتوقع لآخر محطة -->
        <v-col cols="12" sm="6" md="3" class="py-1">
          <v-card class="pa-3 rounded-xl elevation-1 border d-flex align-center justify-space-between">
            <div>
              <div class="text-caption grey--text font-weight-medium">الوصول المتوقع لأخر محطة</div>
              <div class="text-h6 font-weight-bold success--text">{{ formattedEndTime }}</div>
            </div>
            <v-avatar color="green lighten-5" size="42">
              <v-icon color="success">mdi-flag-checkered</v-icon>
            </v-avatar>
          </v-card>
        </v-col>

        <!-- Card 5: مصدر التوقيت -->
        <v-col cols="12" sm="6" md="2" class="py-1">
          <v-card class="pa-3 rounded-xl elevation-1 border d-flex align-center justify-space-between">
            <div>
              <div class="text-caption grey--text font-weight-medium">مصدر التوقيت</div>
              <div class="text-subtitle-1 font-weight-bold" :class="timingSource === 'map' ? 'primary--text' : 'amber--text text--darken-3'">
                {{ timingSource === 'map' ? 'Google Routes' : 'توقيت احتياطي' }}
              </div>
            </div>
            <v-avatar :color="timingSource === 'map' ? 'deep-purple lighten-5' : 'amber lighten-5'" size="42">
              <v-icon :color="timingSource === 'map' ? 'primary' : 'amber darken-3'">
                {{ timingSource === 'map' ? 'mdi-map-marker-radius' : 'mdi-cog-outline' }}
              </v-icon>
            </v-avatar>
          </v-card>
        </v-col>
      </v-row>

      <!-- Main Two-Column Layout -->
      <v-row class="mb-4">
        <!-- Left Panel: Map Preview (52-55%) -->
        <v-col cols="12" lg="6" xl="6">
          <v-card class="pa-4 rounded-xl elevation-1 border fill-height d-flex flex-column">
            <div class="d-flex align-center justify-space-between mb-3">
              <div class="font-weight-bold text-h6 text-dark d-flex align-center">
                <v-icon color="primary" class="mr-2">mdi-map-search-outline</v-icon>
                معاينة المسار على الخريطة
              </div>
            </div>

            <!-- Map Container -->
            <div style="height: 480px; width: 100%; position: relative;" class="rounded-lg overflow-hidden border">
              <GoogleMapLoader
                :enabled="false"
                :center="center"
                :selected="selectedItem"
                :zoom="zoom"
                :apiKey="apiKey"
                :markers="markers"
                :polylines="polyline"
              />
            </div>

            <!-- Map Metrics Bar Below Map -->
            <div class="d-flex align-center justify-space-around grey lighten-5 rounded-lg pa-3 mt-3 border">
              <div class="text-center">
                <div class="text-caption grey--text">محطات التوقف</div>
                <div class="font-weight-bold text-body-1 text-dark d-flex align-center justify-center">
                  <v-icon small color="primary" class="mr-1">mdi-map-marker</v-icon>
                  {{ timelineStops.length }}
                </div>
              </div>

              <v-divider vertical></v-divider>

              <div class="text-center">
                <div class="text-caption grey--text">إجمالي المسافة</div>
                <div class="font-weight-bold text-body-1 text-dark d-flex align-center justify-center">
                  <v-icon small color="blue darken-2" class="mr-1">mdi-road-variant</v-icon>
                  {{ total_distance_km ? total_distance_km + ' كم' : 'غير متاح' }}
                </div>
              </div>

              <v-divider vertical></v-divider>

              <div class="text-center">
                <div class="text-caption grey--text">مدة الطريق</div>
                <div class="font-weight-bold text-body-1 text-dark d-flex align-center justify-center">
                  <v-icon small color="deep-purple" class="mr-1">mdi-clock-outline</v-icon>
                  {{ formattedRoadTime }}
                </div>
              </div>
            </div>

            <!-- Map Legend -->
            <div class="d-flex align-center mt-3 text-caption grey--text text--darken-1">
              <span class="d-flex align-center mr-4">
                <span class="legend-line mr-1"></span> مسار الرحلة
              </span>
              <span class="d-flex align-center mr-4">
                <span class="legend-circle mr-1" style="background-color: #6c5ce7;"></span> بداية الرحلة
              </span>
              <span class="d-flex align-center">
                <span class="legend-circle mr-1" style="background-color: #00b894;"></span> الوجهة النهائية
              </span>
            </div>
          </v-card>
        </v-col>

        <!-- Right Panel: Stop Timeline (45-48%) -->
        <v-col cols="12" lg="6" xl="6">
          <v-card class="pa-4 rounded-xl elevation-1 border fill-height d-flex flex-column">
            <div class="d-flex align-center justify-space-between mb-3">
              <div class="font-weight-bold text-h6 text-dark d-flex align-center">
                <v-icon color="primary" class="mr-2">mdi-bus-stop</v-icon>
                المحطات وأوقات الوصول
              </div>
            </div>

            <!-- Vertical Timeline Scroll Area -->
            <div class="timeline-scroll-container pr-2 flex-grow-1" style="max-height: 520px; overflow-y: auto;">
              <div
                v-for="(stopItem, index) in timelineStops"
                :key="stopItem.stop_id || stopItem.id || index"
                class="timeline-item-wrapper mb-3"
                @click="selectStop(index, stopItem)"
              >
                <v-card
                  outlined
                  class="pa-3 rounded-lg border timeline-stop-card"
                  :class="{
                    'active-stop-card': selectedIdx === index,
                    'final-stop-card': index === timelineStops.length - 1
                  }"
                >
                  <div class="d-flex align-start justify-space-between">
                    <!-- Right/Start side: Sequence badge & details -->
                    <div class="d-flex align-start">
                      <div
                        class="stop-badge-circle mr-3 d-flex align-center justify-center font-weight-bold text-subtitle-2 white--text"
                        :class="getBadgeClass(index, timelineStops.length)"
                      >
                        {{ index + 1 }}
                      </div>

                      <div>
                        <div class="font-weight-bold text-subtitle-1 text-dark">
                          {{ stopItem.stop_name || stopItem.name }}
                        </div>
                        <div class="text-caption grey--text text--darken-1 mb-1">
                          {{ stopItem.address || 'محطة على المسار' }}
                        </div>

                        <!-- Station Wait Badge (Intermediate Stops Only) -->
                        <v-chip
                          v-if="index > 0 && index < timelineStops.length - 1 && ((stopItem.wait_minutes !== undefined && stopItem.wait_minutes > 0) || (trip.station_wait_minutes !== undefined && trip.station_wait_minutes !== null && trip.station_wait_minutes > 0))"
                          x-small
                          color="amber lighten-5"
                          text-color="amber darken-4"
                          class="font-weight-bold"
                        >
                          <v-icon left x-small color="amber darken-3">mdi-timer-outline</v-icon>
                          انتظار {{ stopItem.wait_minutes !== undefined ? stopItem.wait_minutes : (trip.station_wait_minutes !== undefined && trip.station_wait_minutes !== null ? trip.station_wait_minutes : 5) }} دقائق
                        </v-chip>
                        <v-chip
                          v-else-if="index === 0"
                          x-small
                          color="deep-purple lighten-5"
                          text-color="primary"
                          class="font-weight-bold"
                        >
                          محطة البداية
                        </v-chip>
                        <v-chip
                          v-else-if="index === timelineStops.length - 1"
                          x-small
                          color="green lighten-5"
                          text-color="success darken-2"
                          class="font-weight-bold"
                        >
                          الوجهة النهائية
                        </v-chip>
                      </div>
                    </div>

                    <!-- Left/End side: Arrival Time -->
                    <div class="text-left">
                      <div class="text-caption grey--text font-weight-medium mb-1">وقت الوصول</div>
                      <div
                        class="text-h6 font-weight-bold py-1 px-3 rounded-lg d-inline-block"
                        :class="getTimePillClass(index, timelineStops.length)"
                      >
                        {{ getDisplayArrivalTime(stopItem) }}
                      </div>
                    </div>
                  </div>
                </v-card>
              </div>
            </div>

            <!-- Bottom Information Banner -->
            <div
              class="pa-3 mt-3 rounded-lg border d-flex align-center"
              :class="timingSource === 'map' ? 'blue lighten-5 border-blue' : 'amber lighten-5 border-amber'"
            >
              <v-icon :color="timingSource === 'map' ? 'primary' : 'amber darken-3'" class="mr-2">mdi-information-outline</v-icon>
              <div class="text-caption" :class="timingSource === 'map' ? 'primary--text' : 'amber--text text--darken-4'">
                {{ timingSource === 'map'
                  ? 'يتم توزيع أوقات الوصول تلقائياً على جميع المحطات بناءً على الطريق الفعلي على الخريطة.'
                  : 'تعذر حساب المسار من الخريطة وتم استخدام التوقيت الاحتياطي.' }}
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Bottom Trip Settings Card -->
      <v-card class="pa-4 rounded-xl elevation-1 border mb-4">
        <div class="font-weight-bold text-subtitle-1 text-dark mb-3 d-flex align-center">
          <v-icon color="primary" class="mr-2">mdi-cog-outline</v-icon>
          إعدادات الرحلة
        </div>

        <v-row class="align-center">
          <!-- Station Wait Field -->
          <v-col cols="12" md="4" lg="3">
            <v-select
              v-model="trip.station_wait_minutes"
              :items="[0, 2, 3, 5, 7, 10, 15]"
              outlined
              dense
              hide-details
              label="زمن الانتظار في كل محطة (دقيقة)"
              @change="fetchTimingPreview"
              prepend-inner-icon="mdi-clock-time-four-outline"
            ></v-select>
          </v-col>

          <!-- Driver Buffer Read-Only Info -->
          <v-col cols="12" md="4" lg="4">
            <div class="pa-2 px-3 grey lighten-4 rounded-lg border d-flex align-center justify-space-between">
              <span class="text-caption grey--text text--darken-2 d-flex align-center">
                <v-icon small class="mr-1" color="primary">mdi-calendar-clock</v-icon>
                وقت احتياطي بين الرحلات عند تعيين السائق:
              </span>
              <v-chip x-small color="primary" text-color="white" class="font-weight-bold">
                15 دقيقة
              </v-chip>
            </div>
          </v-col>

          <!-- Info Badge Right -->
          <v-col cols="12" md="4" lg="5">
            <div class="pa-2 px-3 green lighten-5 rounded-lg border border-success d-flex align-center">
              <v-icon color="success" class="mr-2">mdi-check-circle-outline</v-icon>
              <span class="text-caption success--text font-weight-medium">
                جميع أوقات الوصول محسوبة تلقائياً من مسار الرحلة بدون إدخال يدوي لأوقات المحطات.
              </span>
            </div>
          </v-col>
        </v-row>
      </v-card>

      <!-- Action Bar -->
      <div class="d-flex align-center justify-space-between pt-2">
        <div>
          <v-btn text color="grey darken-2" @click="back" class="px-4">
            <v-icon left>mdi-arrow-right</v-icon>
            {{ $t('back') }}
          </v-btn>

          <v-btn outlined color="primary" @click="fetchTimingPreview" class="mr-2 px-4 rounded-lg" :loading="loading">
            <v-icon left>mdi-refresh</v-icon>
            إعادة حساب الأوقات
          </v-btn>
        </div>

        <v-btn color="primary" large @click="finish" class="px-6 rounded-lg font-weight-bold elevation-2">
          متابعة
          <v-icon right>mdi-arrow-left</v-icon>
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import GoogleMapLoader from "../../../components/GoogleMapLoader.vue";
import VueElementLoading from "vue-element-loading";

export default {
  components: {
    GoogleMapLoader,
    VueElementLoading,
  },
  props: {
    trip: Object,
    timestep: Number,
    mode: Number,
    apiKey: String,
  },
  data() {
    return {
      loading: true,
      submiting: false,
      timing: null,
      stops: [],
      directions: [],
      polyline: [],
      markers: [],
      total_distance_km: null,
      selectedIdx: 0,
      selectedItem: null,
      center: {
        lat: 30.0444,
        lng: 31.2357,
      },
      zoom: 12,
      colors: [
        "#6c5ce7",
        "#0984e3",
        "#00b894",
        "#e17055",
        "#d63031",
        "#e84393",
      ],
    };
  },
  computed: {
    timingSource() {
      return this.timing ? this.timing.timing_source : "map";
    },
    timelineStops() {
      if (this.timing && this.timing.stops && this.timing.stops.length > 0) {
        return this.timing.stops;
      }
      return this.stops;
    },
    formattedStartTime() {
      if (this.timing && this.timing.trip_start_at) {
        let parts = this.timing.trip_start_at.split(" ");
        return parts[1] ? parts[1].substr(0, 5) : this.trip.first_stop_time;
      }
      return this.trip.first_stop_time || "12:00";
    },
    formattedEndTime() {
      if (this.timing && this.timing.expected_end_at) {
        let parts = this.timing.expected_end_at.split(" ");
        return parts[1] ? parts[1].substr(0, 5) : "N/A";
      }
      return "N/A";
    },
    formattedRoadTime() {
      if (this.timing && this.timing.total_road_minutes !== undefined) {
        let mins = parseInt(this.timing.total_road_minutes);
        let hrs = Math.floor(mins / 60);
        let remMins = mins % 60;
        if (hrs > 0) {
          return `${hrs} س ${remMins} د`;
        }
        return `${mins} د`;
      }
      return "N/A";
    },
    formattedWaitTime() {
      if (this.timing && this.timing.total_wait_minutes !== undefined) {
        return `${this.timing.total_wait_minutes} د`;
      }
      let waitMin = this.trip.station_wait_minutes !== undefined && this.trip.station_wait_minutes !== null ? parseInt(this.trip.station_wait_minutes) : 5;
      let count = Math.max(0, this.timelineStops.length - 2);
      return `${waitMin * count} د`;
    },
  },
  mounted() {
    if (this.trip.station_wait_minutes === undefined || this.trip.station_wait_minutes === null) {
      this.$set(this.trip, "station_wait_minutes", 5);
    }
    this.fetchTimingPreview();
  },
  methods: {
    fetchTimingPreview() {
      this.loading = true;
      let routeId = this.trip.route ? this.trip.route.id : this.trip.route_id;
      let waitMin = this.trip.station_wait_minutes !== undefined && this.trip.station_wait_minutes !== null ? parseInt(this.trip.station_wait_minutes) : 5;

      axios
        .post("/trips/timing-preview", {
          route_id: routeId,
          first_stop_time: this.trip.first_stop_time,
          effective_date: this.trip.effective_date,
          station_wait_minutes: waitMin,
          stop_to_stop_avg_time: this.trip.stop_to_stop_avg_time || 15,
        })
        .then((response) => {
          this.loading = false;
          let data = response.data;
          this.timing = data.timing;
          this.total_distance_km = data.total_distance_km;
          this.stops = data.stops || [];
          this.directions = data.directions || [];

          // Synchronize trip object's inter_time & arrival times for final save
          if (this.timing && this.timing.stops) {
            this.trip.inter_time = [];
            this.trip.time = [];
            this.timing.stops.forEach((st) => {
              this.trip.inter_time.push(st.road_minutes_from_previous || 0);
              this.trip.time.push(st.arrival_time_str);
            });
          }

          this.setupMarkersAndPolylines();
        })
        .catch((error) => {
          this.loading = false;
          console.error("Error fetching timing preview:", error);
          this.$notify({
            title: "Warning",
            text: "تعذر استدعاء التوقيت الذكي من الخريطة، تم تفعيل التوقيت الاحتياطي.",
            type: "warning",
          });
        });
    },
    setupMarkersAndPolylines() {
      this.markers = [];
      this.polyline = [];

      let stopsToUse = this.timelineStops;
      if (stopsToUse.length > 0) {
        this.center = {
          lat: parseFloat(stopsToUse[0].lat),
          lng: parseFloat(stopsToUse[0].lng),
        };
      }

      stopsToUse.forEach((stop, index) => {
        let pos = {
          lat: parseFloat(stop.lat),
          lng: parseFloat(stop.lng),
        };
        this.markers.push({
          place_id: stop.place_id || stop.stop_id || index,
          position: pos,
          infoText: `<div class='pa-2'><strong>${index + 1}. ${stop.stop_name || stop.name}</strong><br/>${stop.arrival_time_str || ''}</div>`,
        });
      });

      if (this.directions && this.directions.length > 0) {
        for (let i = 0; i < this.directions.length; i++) {
          for (let j = 0; j < this.directions[i].length; j++) {
            const direction = this.directions[i][j];
            if (direction.current === 1 && direction.overview_path) {
              this.polyline.push({
                data: direction.overview_path,
                strokeColor: "#6c5ce7",
                strokeWeight: 4,
              });
            }
          }
        }
      }
    },
    selectStop(index, stopItem) {
      this.selectedIdx = index;
      this.selectedItem = stopItem.place_id || stopItem.stop_id || index;
      if (stopItem.lat && stopItem.lng) {
        this.center = {
          lat: parseFloat(stopItem.lat),
          lng: parseFloat(stopItem.lng),
        };
      }
    },
    getBadgeClass(index, total) {
      if (index === 0) return "bg-purple-primary";
      if (index === total - 1) return "bg-green-success";
      return "bg-purple-primary";
    },
    getTimePillClass(index, total) {
      if (index === 0) return "purple lighten-5 primary--text";
      if (index === total - 1) return "green lighten-5 success--text text--darken-2";
      return "grey lighten-4 text-dark";
    },
    getDisplayArrivalTime(stopItem) {
      if (stopItem.arrival_time_str) {
        return stopItem.arrival_time_str;
      }
      if (stopItem.arrival_at) {
        let parts = stopItem.arrival_at.split(" ");
        return parts[1] ? parts[1].substr(0, 5) : "12:00";
      }
      return "12:00";
    },
    finish() {
      this.$emit("finish");
    },
    back() {
      this.$emit("back");
    },
  },
};
</script>

<style scoped>
.smart-schedule-container {
  font-family: inherit;
}

.border {
  border: 1px solid #e2e8f0 !important;
}
.border-success {
  border: 1px solid #a7f3d0 !important;
}
.border-warning {
  border: 1px solid #fde68a !important;
}
.border-amber {
  border: 1px solid #fef3c7 !important;
}
.border-error {
  border: 1px solid #fecaca !important;
}
.border-blue {
  border: 1px solid #bfdbfe !important;
}

.bg-purple-primary {
  background-color: #6c5ce7 !important;
}
.bg-green-success {
  background-color: #00b894 !important;
}

.stop-badge-circle {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  flex-shrink: 0;
}

.timeline-stop-card {
  transition: all 0.2s ease-in-out;
  cursor: pointer;
}
.timeline-stop-card:hover {
  border-color: #6c5ce7 !important;
  box-shadow: 0 4px 12px rgba(108, 92, 231, 0.08) !important;
}
.active-stop-card {
  border-color: #6c5ce7 !important;
  background-color: #f8f7ff !important;
}

.legend-line {
  display: inline-block;
  width: 20px;
  height: 3px;
  border-radius: 2px;
  background-color: #6c5ce7;
}
.legend-circle {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
}
</style>
